import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import apiClient from "services/ApiClient";
import { SSOProviderConfig, SSOProviderFormValues, SCIMTokenResponse } from "../models";

export const ssoProviderKeys = {
  all: ["ssoProvider"] as const,
  detail: () => [...ssoProviderKeys.all, "detail"] as const,
};

// Fetch SSO provider configuration
export const useSSOProviderQuery = () => useQuery<SSOProviderConfig | null>({
  queryKey: ssoProviderKeys.detail(),
  queryFn: async () => apiClient.get<SSOProviderConfig | null>(API_ROUTES.ssoProvider.base),
  staleTime: 1000 * 60 * 3, // 3 minutes
});

// Create SSO provider
export const useCreateSSOProvider = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: SSOProviderFormValues) => {
      // Convert allowedDomains from string to array
      const { allowedDomains: allowedDomainsString, ...restData } = data;
      const allowedDomains = allowedDomainsString
        ? allowedDomainsString.split("\n").filter((domain) => domain.trim() !== "")
        : [];

      return apiClient.post(API_ROUTES.ssoProvider.base, {
        ssoProvider: {
          ...restData,
          allowedDomains,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ssoProviderKeys.all });
      toast({
        title: "SSO Provider Created",
        description: "Your SSO configuration has been saved successfully.",
        status: "success",
        duration: 5000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error Creating SSO Provider",
        description: error.message || "There was an error creating your SSO configuration.",
        status: "error",
        duration: 5000,
      });
    },
  });
};

// Update SSO provider
export const useUpdateSSOProvider = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: SSOProviderFormValues) => {
      // Convert allowedDomains from string to array
      const { allowedDomains: allowedDomainsString, ...restData } = data;
      const allowedDomains = allowedDomainsString
        ? allowedDomainsString.split("\n").filter((domain) => domain.trim() !== "")
        : [];

      // Build update data with converted allowedDomains
      const updateData: any = {
        ...restData,
        allowedDomains,
      };

      // Remove empty encrypted fields to keep existing values
      if (data.protocol === "oidc") {
        if (!data.clientId || data.clientId === "") {
          delete updateData.clientId;
        }
        if (!data.clientSecret || data.clientSecret === "") {
          delete updateData.clientSecret;
        }
      }

      return apiClient.put(API_ROUTES.ssoProvider.base, {
        ssoProvider: updateData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ssoProviderKeys.all });
      toast({
        title: "SSO Provider Updated",
        description: "Your SSO configuration has been updated successfully.",
        status: "success",
        duration: 5000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error Updating SSO Provider",
        description: error.message || "There was an error updating your SSO configuration.",
        status: "error",
        duration: 5000,
      });
    },
  });
};

// Delete SSO provider
export const useDeleteSSOProvider = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: () => apiClient.delete(API_ROUTES.ssoProvider.base),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ssoProviderKeys.all });
      toast({
        title: "SSO Provider Deleted",
        description: "Your SSO configuration has been removed.",
        status: "success",
        duration: 5000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error Deleting SSO Provider",
        description: error.message || "There was an error deleting your SSO configuration.",
        status: "error",
        duration: 5000,
      });
    },
  });
};

// Generate SCIM token
export const useGenerateSCIMToken = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: () => apiClient.post<SCIMTokenResponse>(`${API_ROUTES.ssoProvider.base}/scim-token`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ssoProviderKeys.all });
      toast({
        title: "SCIM Token Generated",
        description: "Your SCIM token has been generated. Make sure to copy it now as it won't be shown again.",
        status: "success",
        duration: 8000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error Generating SCIM Token",
        description: error.message || "There was an error generating your SCIM token.",
        status: "error",
        duration: 5000,
      });
    },
  });
};

// Disable SCIM provisioning
export const useDisableSCIM = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: () => apiClient.delete(`${API_ROUTES.ssoProvider.base}/scim-token`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ssoProviderKeys.all });
      toast({
        title: "SCIM Disabled",
        description: "SCIM provisioning has been disabled.",
        status: "success",
        duration: 5000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error Disabling SCIM",
        description: error.message || "There was an error disabling SCIM provisioning.",
        status: "error",
        duration: 5000,
      });
    },
  });
};

// Parse metadata URL to extract SSO configuration
export const useParseMetadata = () => {
  const toast = useToast();

  return useMutation({
    mutationFn: ({ metadataUrl, protocol }: { metadataUrl: string; protocol?: string }) => apiClient.post<any>(`${API_ROUTES.ssoProvider.base}/parse-metadata`, {
      metadata_url: metadataUrl,
      protocol,
    }),
    onSuccess: () => {
      toast({
        title: "Metadata Fetched Successfully",
        description: "Configuration fields have been auto-populated. Review and save when ready.",
        status: "success",
        duration: 5000,
      });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errors?.metadata_url?.[0] || error.message || "Failed to fetch metadata from the provided URL.";
      toast({
        title: "Error Fetching Metadata",
        description: errorMessage,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    },
  });
};
